import {
  Facebook,
  Youtube,
  Linkedin,
  Instagram,
  Twitter,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ROUTING_PATH, FILTER_KEYS, PROJECT_NAME } from "../core/config/Constant";
import { useSiteConfig } from "../features/static/hooks/useSiteConfig";
import { getStaticPage } from "../features/static/services/staticPagesService";
import { STATIC_PAGE_TYPE } from "../core/config/Constant";
import FeedbackWidget from "../features/feedback/components/FeedbackWidget";

const Footer = () => {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  const handleCityClick = (cityName) => {
    window.scrollTo(0, 0);
    navigate(`${ROUTING_PATH.PROPERTY}?${FILTER_KEYS.CITY}=${encodeURIComponent(cityName)}`);
  };

  // Social + contact values are CMS-managed; appstore/playstore aren't part of that API.
  const { data: siteConfig = {} } = useSiteConfig();
  const socialLinks = {
    facebook: siteConfig.facebook || "#",
    youtube: siteConfig.youtube || "#",
    linkedin: siteConfig.linkedin || "#",
    instagram: siteConfig.instagram || "#",
    twitter: siteConfig.twitter || "#",
    appstore: "#",
    playstore: "https://play.google.com/store/apps/details?id=com.reelty.newapp.in&hl=en_IN",
  };

  const appDownloadLinks = [
    {
      label: "Download on the App Store",
      href: socialLinks.appstore,
      image: "/image/applestore.png",
    },
    {
      label: "Get it on Google Play",
      href: socialLinks.playstore,
      image: "/image/googlestore.png",
    },
  ];

  const handleSocialClick = async (type) => {
    try {
      const response = await getStaticPage(type);
      const url = response[type];
      if (url && url !== "#") {
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Failed to fetch social link:", error);
    }
  };

  const socialIcons = [
    { icon: Facebook, type: STATIC_PAGE_TYPE.FACEBOOK, label: "Facebook" },
    { icon: Instagram, type: STATIC_PAGE_TYPE.INSTAGRAM, label: "Instagram" },
    { icon: Youtube, type: STATIC_PAGE_TYPE.YOUTUBE, label: "YouTube" },
    { icon: Linkedin, type: STATIC_PAGE_TYPE.LINKEDIN, label: "LinkedIn" },
    { icon: Twitter, type: STATIC_PAGE_TYPE.TWITTER, label: "Twitter" },
  ];

  const companyLinks = [
    { label: "About Us", path: ROUTING_PATH.ABOUT_US },
    { label: "Contact Us", path: ROUTING_PATH.CONTACT },
    { label: "FAQ", path: ROUTING_PATH.FAQ },
    { label: "Privacy Policy", path: ROUTING_PATH.PRIVACY_POLICY },
    { label: "Terms of Service", path: ROUTING_PATH.TERMS_AND_CONDITIONS },
  ];

  const propertyLinks = [
    { label: "Properties in Delhi", city: "Delhi" },
    { label: "Properties in Mumbai", city: "Mumbai" },
    { label: "Properties in Bengaluru", city: "Bengaluru" },
    { label: "Properties in Pune", city: "Pune" },
    { label: "Properties in Hyderabad", city: "Hyderabad" },
    { label: "Properties in Chennai", city: "Chennai" },
  ];

  const quickLinks = [
    { label: "Buy Property", path: `${ROUTING_PATH.PROPERTY}?${FILTER_KEYS.CATEGORY_TYPE}=buy` },
    { label: "Rent Property", path: `${ROUTING_PATH.PROPERTY}?${FILTER_KEYS.CATEGORY_TYPE}=rent` },
    { label: "Post Property", path: ROUTING_PATH.ADD_NEW_PROPERTY },
    { label: "All Offers", path: ROUTING_PATH.ALL_OFFERS },
    { label: "Builder Projects", path: `${ROUTING_PATH.PROJECT_LIST}?type=view_all` },
  ];

  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gray-900 text-gray-300 overflow-hidden">
      {/* Decorative gradient bar at top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700" />


      {/* ─── Main Footer Grid ─── */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Column 1 — Brand */}
          <div className="lg:col-span-4">
            <img
              src="/image/reelty_Logo2-white.svg"
              alt={`${PROJECT_NAME} Logo`}
              className="h-12 w-auto mb-5 object-contain"
            />
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
              Your trusted partner in finding the perfect property.
              We offer end-to-end real estate services including home loans,
              legal assistance, property valuation, and expert advice.
            </p>

            {/* Contact details (CMS-managed) */}
            {(siteConfig.emails || siteConfig.contact_number) && (
              <div className="mb-5 space-y-1.5 text-sm text-gray-400">
                {siteConfig.emails && (
                  <a href={`mailto:${siteConfig.emails}`} className="block hover:text-white transition-colors no-underline">
                    {siteConfig.emails}
                  </a>
                )}
                {siteConfig.contact_number && (
                  <a href={`tel:${siteConfig.contact_number}`} className="block hover:text-white transition-colors no-underline">
                    {siteConfig.contact_number}
                  </a>
                )}
              </div>
            )}

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              {socialIcons.map(({ icon: Icon, type, label }, index) => (
                <button
                  key={index}
                  onClick={() => handleSocialClick(type)}
                  aria-label={label}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-800 text-gray-400 hover:bg-primary-600 hover:text-white transition-all duration-300"
                >
                  <Icon size={17} />
                </button>
              ))}
            </div>
          </div>

          {/* Column 2 — Company */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5">
              Company
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white text-sm cursor-pointer transition-colors duration-200 flex items-center gap-2 group no-underline hover:no-underline"
                  >
                    <ChevronRight size={14} className="text-gray-600 group-hover:text-primary-500 transition-colors" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((item) => (
                <li key={item.label}>
                  <span
                    onClick={() => handleNavigation(item.path)}
                    className="text-gray-400 hover:text-white text-sm cursor-pointer transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <ChevronRight size={14} className="text-gray-600 group-hover:text-primary-500 transition-colors" />
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — Properties in India */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5">
              Properties
            </h4>
            <ul className="space-y-3">
              {propertyLinks.map((item) => (
                <li key={item.city}>
                  <span
                    onClick={() => handleCityClick(item.city)}
                    className="text-gray-400 hover:text-white text-sm cursor-pointer transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <ChevronRight size={14} className="text-gray-600 group-hover:text-primary-500 transition-colors" />
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5 — Download App */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5">
              Get the App
            </h4>
              <div className="flex flex-col gap-3 mb-5">
                <a
                  href={socialLinks?.appstore || "#"}
                  target={socialLinks?.appstore === "#" ? undefined : "_blank"}
                  rel={socialLinks?.appstore === "#" ? undefined : "noopener noreferrer"}
                  aria-label="Download on the App Store"
                  className="inline-flex h-14 w-44 items-center justify-center gap-2.5 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors shadow-sm"
                >
                  <svg viewBox="0 0 384 512" fill="currentColor" className="w-7 h-7">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                  </svg>
                  <div className="flex flex-col items-start justify-center pt-0.5">
                    <span className="text-[10px] leading-[1] font-medium opacity-90">Download on the</span>
                    <span className="text-lg font-bold leading-[1.2]">App Store</span>
                  </div>
                </a>
                
                <a
                  href={socialLinks?.playstore || "#"}
                  target={socialLinks?.playstore === "#" ? undefined : "_blank"}
                  rel={socialLinks?.playstore === "#" ? undefined : "noopener noreferrer"}
                  aria-label="Get it on Google Play"
                  className="inline-flex h-14 w-44 items-center justify-center gap-2.5 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors shadow-sm"
                >
                  <svg viewBox="0 0 512 512" fill="currentColor" className="w-7 h-7">
                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                  </svg>
                  <div className="flex flex-col items-start justify-center pt-0.5">
                    <span className="text-[10px] leading-[1] font-medium opacity-90">GET IT ON</span>
                    <span className="text-lg font-bold leading-[1.2]">Google Play</span>
                  </div>
                </a>
              </div>

            {/* QR Code */}
            <div className="flex items-center gap-4 bg-gray-800/60 border border-primary-500/20 p-4 rounded-2xl">
              <div className="shrink-0 bg-white p-1.5 rounded-lg">
                <img
                  src="/image/qrbar.png"
                  alt="QR Code"
                  className="h-14 w-14 rounded object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-white leading-snug">
                  Scan to Download
                </p>
                <p className="text-xs text-gray-400 leading-relaxed mt-0.5">
                  Get the {PROJECT_NAME} app on your phone
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Bottom Bar ─── */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              © {currentYear} {PROJECT_NAME}. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to={ROUTING_PATH.PRIVACY_POLICY}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-gray-300 no-underline hover:no-underline transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to={ROUTING_PATH.TERMS_AND_CONDITIONS}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-gray-300 no-underline hover:no-underline transition-colors"
            >
              Terms of Service
            </Link>

          </div>
        </div>
      </div>

      {/* ─── Feedback Widget (floating) ─── */}
      <FeedbackWidget />
    </footer>
  );
};

export default Footer;
